import { useState } from 'react';
import { Button, Loader, Box, Text } from '@mantine/core';
import { Plus } from 'lucide-react';
import { useCreateNote } from '../../hooks/useNotes';
import { useUser } from '../../contexts/UserContext';

interface AddNoteButtonProps {
  onNoteCreated?: (noteId: number) => void;
  defaultColor?: string;
  isMobile?: boolean;
}

export const AddNoteButton = ({ 
  onNoteCreated, 
  defaultColor = "#FFE066",
  isMobile = false
}: AddNoteButtonProps) => {
  const [isCreating, setIsCreating] = useState(false);
  const createNoteMutation = useCreateNote();
  const { currentUser } = useUser();

  const handleAddNote = async () => {
    if (!currentUser?.email || isCreating) return;

    setIsCreating(true);
    
    try {
      // Generate random position to avoid overlapping notes
      const randomX = Math.floor(Math.random() * 300) + 50;
      const randomY = Math.floor(Math.random() * 200) + 100;

      const result = await createNoteMutation.mutateAsync({
        content: '',
        title: '',
        color: defaultColor,
        positionX: randomX,
        positionY: randomY,
        width: 250,
        height: 200,
      });

      // Call the callback with the new note ID so it can be focused
      onNoteCreated?.(result.id);
    } catch (error) {
      console.error('Failed to create note:', error);
    } finally {
      setIsCreating(false);
    }
  };

  // Mobile: Compact button to match search button
  if (isMobile) {
    return (
      <Button
        onClick={handleAddNote}
        disabled={!currentUser?.email || isCreating}
        variant="default"
        size="compact-sm"
        px={8}
        title="Add new note"
        style={{ 
          aspectRatio: '1',
          backgroundColor: isCreating ? defaultColor : 'var(--app-surface)',
          borderColor: 'var(--app-border)',
          color: isCreating ? '#000' : 'var(--app-text-primary)',
          transition: 'all 0.2s ease',
        }}
        onMouseEnter={(e) => {
          if (!isCreating) {
            e.currentTarget.style.backgroundColor = defaultColor;
            e.currentTarget.style.color = '#000';
            e.currentTarget.style.transform = 'scale(1.05)';
          }
        }}
        onMouseLeave={(e) => {
          if (!isCreating) {
            e.currentTarget.style.backgroundColor = 'var(--app-surface)';
            e.currentTarget.style.color = 'var(--app-text-primary)';
            e.currentTarget.style.transform = 'scale(1)';
          }
        }}
      >
        {isCreating ? (
          <Loader size="sm" color="#000" />
        ) : (
          <Plus size={12} />
        )}
      </Button>
    );
  }

  // Desktop: Compact button for control bar
  return (
    <Button
      onClick={handleAddNote}
      disabled={!currentUser?.email || isCreating}
      variant="filled"
      size="sm"
      style={{
        background: isCreating 
          ? `linear-gradient(135deg, ${defaultColor} 0%, #FFD93D 100%)`
          : `linear-gradient(135deg, ${defaultColor} 0%, #FFD93D 100%)`,
        border: 'none',
        color: '#000',
        fontWeight: 600,
        fontSize: '14px',
        borderRadius: '8px',
        boxShadow: '0 2px 6px rgba(255, 224, 102, 0.25)',
        transition: 'all 0.2s ease',
        position: 'relative',
        overflow: 'hidden',
        height: '32px',
        minHeight: '32px',
      }}
      onMouseEnter={(e) => {
        if (!isCreating) {
          e.currentTarget.style.transform = 'scale(1.05)';
          e.currentTarget.style.boxShadow = '0 4px 12px rgba(255, 224, 102, 0.35)';
        }
      }}
      onMouseLeave={(e) => {
        if (!isCreating) {
          e.currentTarget.style.transform = 'scale(1)';
          e.currentTarget.style.boxShadow = '0 2px 6px rgba(255, 224, 102, 0.25)';
        }
      }}
    >
      <Box style={{ 
        display: 'flex', 
        alignItems: 'center', 
        gap: '6px',
        position: 'relative',
        zIndex: 2
      }}>
        {isCreating ? (
          <>
            <Loader size="xs" color="#000" />
            <Text size="sm" fw={600} c="#000">Creating...</Text>
          </>
        ) : (
          <>
            <Plus size={16} />
            <Text size="sm" fw={600} c="#000">Add Note</Text>
          </>
        )}
      </Box>
    </Button>
  );
};

export default AddNoteButton;